<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Períodos de Avaliações</h5>
      <b-button
        v-if="hasPermission('adicionar_periodos_avaliacoes')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Período"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        class="form-control"
        placeholder="Filtrar por Nome"
        @input="filtrar"
      />
      <b-form-select
        v-model="filters.vigencia"
        :options="[
          { value: 'todas', text: 'Todas' },
          { value: 'vigentes', text: 'Vigentes' }
        ]"
        @change="filtrar"
        class="w-auto vigencia-select ms-2"
      />
      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('id')">
              ID {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('data_inicio')">
              Início {{ arrow('data_inicio') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('data_fim')">
              Fim {{ arrow('data_fim') }}
            </button>
          </th>
          <th>Alunos</th>
          <th>Realizadas</th>
          <th>Em Supervisão</th>
          <th>Finalizadas</th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('aplicada')">
              Aplicada {{ arrow('aplicada') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in periodos" :key="p.id">
          <td>{{ p.id }}</td>
          <td>{{ p.nome }}</td>
          <td>{{ p.data_inicio }}</td>
          <td>{{ p.data_fim }}</td>
          <td>{{ p.alunos }}</td>
          <td>{{ p.realizadas }}</td>
          <td>{{ p.em_supervisao }}</td>
          <td>{{ p.finalizadas }}</td>
          <td>{{ p.aplicada }}</td>
          <td class="text-end">
            <!-- Detalhar -->
            <router-link
              v-if="hasPermission('detalhar_periodos_avaliacoes')"
              :to="`/cadastros/avaliacoes-periodos/${p.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-1" title="Detalhar"></i>
            </router-link>
            <!-- Aplicar -->
            <a
              v-if="hasPermission('aplicar_periodos_avaliacoes')"
              :class="{ 'disabled-icon': p.aplicada==='Sim' }"
              href="#"
              @click.prevent="p.aplicada==='Sim' ? null : abrirModal(p.id)"
            >
              <i
                class="bi bi-check2-circle mx-1"
                :class="p.aplicada==='Sim' ? 'text-secondary' : 'text-success'"
                :title="p.aplicada==='Sim' ? 'Já aplicada' : 'Aplicar Avaliação'"
              ></i>
            </a>
            <!-- Ampliar prazo (permitido mesmo aplicada) -->
            <a
              v-if="hasPermission('alterar_periodos_avaliacoes')"
              href="#"
              @click.prevent="abrirModalPrazo(p)"
              :title="`Ampliar prazo (Fim: ${p.data_fim})`"
            >
              <i class="bi bi-calendar-plus text-success mx-1"></i>
            </a>
            <!-- Editar -->
            <router-link
              v-if="hasPermission('alterar_periodos_avaliacoes') && p.aplicada==='Não'"
              :to="`/cadastros/avaliacoes-periodos/${p.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-1" title="Editar"></i>
            </router-link>
            <span
              v-else-if="hasPermission('alterar_periodos_avaliacoes')"
              class="disabled-icon"
            >
              <i class="bi bi-pencil-fill text-secondary mx-1" title="Não editável"></i>
            </span>
            <!-- Excluir -->
            <i
              v-if="hasPermission('excluir_periodos_avaliacoes') && p.aplicada==='Não'"
              class="bi bi-trash-fill text-danger mx-1"
              @click="excluir(p.id)"
              title="Excluir"
            ></i>
            <i
              v-else-if="hasPermission('excluir_periodos_avaliacoes')"
              class="bi bi-trash-fill text-secondary mx-1 disabled-icon"
              title="Não excluível"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>

    <!-- Modal de Aplicação -->
    <ModalAplicarAvaliacao
      v-if="modalAplicar"
      :visible="modalAplicar"
      :periodo="periodoSelecionado"
      :associacoes="associacoesDetalhadas"
      @fechar="modalAplicar = false"
      @aplicado="handleAplicacao"
    />
    <ModalEstenderPrazoAvaliacao
      :mostrar="modalPrazo"
      :periodo="periodoPrazo"
      @fechar="modalPrazo = false"
      @salvou="onPrazoSalvo"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'
import ModalAplicarAvaliacao from '@/components/modals/ModalAplicarAvaliacao.vue'
import ModalEstenderPrazoAvaliacao from '@/components/modals/ModalEstenderPrazoAvaliacao.vue'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

// listagem/paginação/ordenação
const {
  items: periodos,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/avaliacoes-periodos/listagem`,
  { key: 'nome', order: 'asc' }
)

const pageOptions = [10,15,20,25,50]
const startRow = computed(() => (page.value-1)*limit.value+1)
const endRow   = computed(() => Math.min(page.value*limit.value, totalItems.value))

const modalPrazo = ref(false)
const periodoPrazo = ref(null)

function abrirModalPrazo(p) {
  // Você pode usar o item da própria grid (datas vêm como DD/MM/YYYY, o modal lida com isso)
  periodoPrazo.value = p
  modalPrazo.value = true
}
function onPrazoSalvo() {
  modalPrazo.value = false
  proxy.$toast.success('Prazo atualizado')
  load() // já existente
}

watch(page,  (n,o)=> n!==o && load())
watch(limit,(n,o)=>{ if(n!==o){ page.value=1; load() }})

// filtros
function filtrar() {
  page.value = 1; load()
}
function limparFiltros() {
  filters.value = { nome: '', vigencia: 'todas' }
  page.value = 1; load()
}
function changeLimit() {
  page.value = 1; load()
}

// ordenação manual (chama composable)
function changeSort(field) {
  sort(field)
}

// CRUD e navegação
function irParaCadastro() {
  router.push('/cadastros/avaliacoes-periodos/novo')
}
async function excluir(id) {
  const ok = await proxy.$confirm('Excluir período?', 'Confirmação')
  if(!ok) return
  await axios.delete(`/cadastros/avaliacoes-periodos/${id}`)
  proxy.$toast.success('Período excluído')
  load()
}

// modal aplicar
const modalAplicar = ref(false)
const periodoSelecionado = ref(null)
const associacoesDetalhadas = ref([])

async function abrirModal(id) {
  const { data } = await axios.get(`/cadastros/avaliacoes-periodos/${id}`)
  periodoSelecionado.value = data

  const [nivRes, formRes] = await Promise.all([
    axios.get('/cadastros/niveis-turmas/todos',{params:{ativo:true}}),
    axios.get('/configuracoes/formularios-avaliacoes/todos')
  ])
  associacoesDetalhadas.value = (data.niveis||[]).map(a=>({
    nivelNome: nivRes.data.data.find(n=>n.id===a.nivel_id)?.nivel||`ID:${a.nivel_id}`,
    formularioNome: formRes.data.data.find(f=>f.id===a.avaliacoes_formularios_id)?.formulario||`ID:${a.avaliacoes_formularios_id}`
  }))
  modalAplicar.value = true
}

function handleAplicacao() {
  modalAplicar.value = false
  load()
}

onMounted(async ()=>{
  await fetchPermissions()
  filters.value = { nome: '', vigencia: 'todas' }
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: .8rem !important;
  border-radius: 0 !important;
}
.bv-table-sort-icon { display: none !important; }
.disabled-icon { pointer-events: none; opacity: .6; }

.vigencia-select {
  min-width: 150px; /* ajuste fino: pode testar 140–170px */
}

</style>
